import { Suspense, useEffect } from 'react'
import { Provider } from 'react-redux'
import { RouterProvider } from 'react-router-dom'
import CircularProgress from '@mui/material/CircularProgress'
import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles'
import api from './api'
import styles from './app.module.scss'
import { router } from './appRouter'
import Snackbar from './components/Common/Snackbar'
import EnableNotificationsBanner from './components/EnableNotificationsBanner'
import EnvironmentBanner from './components/EnvironmentBanner'
import MessageToasts from './components/MessageToasts'
import NavigationProgress from './components/NavigationProgress'
import UnreadBadge from './components/UnreadBadge'
import { ColorModeProvider } from './contexts/ColorModeContext'
import { PageTransitionProvider } from './contexts/PageTransitionContext'
import { RefreshProvider } from './contexts/RefreshContext'
import { isInAtlasModuleFrame } from './helpers/atlasModuleFrame'
import { startClarityWhenIdle } from './helpers/clarity'
import { PermissionsProvider } from './helpers/permissionContext'
import theme from './helpers/theme'
import configureStore from './redux/store'

export const store = configureStore(api)

// A record in AIM's module panel: the tab around it already records the session and owns
// the banners, badge and toasts.
const isModuleFrame = isInAtlasModuleFrame()

// Simple router component - React Router handles error boundaries now.
// The Suspense boundary catches lazy route chunks rendered outside PrivateRoute
// (login/auth screens and layout children), which carry their own boundary.
const AppRouter = () => {
  return (
    <Suspense fallback={<CircularProgress size="large" className={styles.suspenseFallback} />}>
      <RouterProvider router={router} />
    </Suspense>
  )
}

const App = () => {
  useEffect(() => {
    if (!isModuleFrame) startClarityWhenIdle()
  }, [])

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <Provider store={store}>
          <ColorModeProvider>
            <PermissionsProvider>
              <PageTransitionProvider>
                <RefreshProvider>
                  <CssBaseline />
                  <Snackbar />
                  {!isModuleFrame && (
                    <>
                      <EnvironmentBanner />
                      <EnableNotificationsBanner />
                      <UnreadBadge />
                      <MessageToasts />
                    </>
                  )}
                  <NavigationProgress />
                  <AppRouter />
                </RefreshProvider>
              </PageTransitionProvider>
            </PermissionsProvider>
          </ColorModeProvider>
        </Provider>
      </ThemeProvider>
    </StyledEngineProvider>
  )
}

export default App
